import React from "react";

const sizes = {
    xs: "w-3 h-3",
    sm: "w-4 h-4",
    md: "w-6 h-6",
    lg: "w-8 h-8",
    xl: "w-12 h-12",
};

const colors = {
    primary: "text-accent-600",
    white: "text-gray-900",
    muted: "text-gray-500",
};

export default function LoadingSpinner({
    size = "md",
    color = "white",
    className = "",
}) {
    const sizeClasses = sizes[size] || sizes.md;
    const colorClasses = colors[color] || colors.white;

    return (
        <svg
            className={`animate-spin ${sizeClasses} ${colorClasses} ${className}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
            />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );
}

export function PageLoader({ message = "Loading..." }) {
    return (
        <div className="min-h-[400px] flex flex-col items-center justify-center">
            <LoadingSpinner size="xl" />
            <p className="mt-6 text-gray-500 font-medium">{message}</p>
        </div>
    );
}

export function InlineLoader({ message = "Loading..." }) {
    return (
        <div className="flex items-center gap-3 text-gray-500">
            <LoadingSpinner size="sm" />
            <span className="text-sm">{message}</span>
        </div>
    );
}

export function LoadingOverlay({ message = "Loading...", visible = true }) {
    if (!visible) return null;

    return (
        <div className="absolute inset-0 bg-white/90 flex flex-col items-center justify-center z-50 rounded-xl">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-700 font-medium">{message}</p>
        </div>
    );
}

export function Skeleton({ className = "", variant = "text" }) {
    const baseClasses = "animate-pulse bg-gray-100 rounded-lg";

    const variantClasses = {
        text: "h-4 w-full",
        title: "h-6 w-3/4",
        avatar: "w-10 h-10 rounded-full",
        button: "h-10 w-24 rounded-lg",
        card: "h-32 w-full rounded-xl",
    };

    return (
        <div
            className={`${baseClasses} ${variantClasses[variant] || variantClasses.text} ${className}`}
        />
    );
}

export function CardSkeleton() {
    return (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
            <div className="flex items-center justify-between">
                <Skeleton variant="title" className="w-1/3" />
                <Skeleton variant="button" />
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="pt-4 flex gap-3">
                <Skeleton variant="avatar" />
                <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-1/4" />
                    <Skeleton className="h-3 w-1/2" />
                </div>
            </div>
        </div>
    );
}

export function StatsSkeleton({ count = 4 }) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
                    <Skeleton className="h-4 w-20 mb-3" />
                    <Skeleton className="h-8 w-32 mb-2" />
                    <Skeleton className="h-4 w-16" />
                </div>
            ))}
        </div>
    );
}
